import { authorizedFetch } from "@/utils/fetch";
import * as authService from "@/services/auth";
import { Comment } from "@/services/comments";

const baseUrl = import.meta.env.VITE_API_URL;
const baseUrlV1 = `${baseUrl}/api/v1`;

const urls = {
  profile: `${baseUrlV1}/profile`,
  users: `${baseUrlV1}/app-users`,
  socialMediaUsers: `${baseUrlV1}/social-media-users`,
  adminSocialMediaUsers: `${baseUrlV1}/admin/social-media-users`,
} as const;

export enum UserRole {
  ADMIN = 1,
  EDITOR = 2,
  SUPPORTER = 3,
  ENGINEER = 4,
}

export enum BlockDuration {
  DAY = "1 day",
  MONTH = "1 month",
  FOREVER = "forever",
}

export type User = {
  id: number;
  subjectId: string;
  email: string;
  firstName: string;
  lastName: string;
  roleId: UserRole;
  img?: string;
  tenant?: string;
};

export type UserBlockStatus = {
  blockStatus: string;
  blockDuration?: string;
  requestTime?: string;
  startDate?: string;
  duration?: string;
  reason?: string;
  additionalInfo?: string;
  timeToExpire?: number;
  requestedBy?: {
    id: number;
    name: string;
  };
  requestBy?: {
    id: string;
    name: string;
  };
};

export type SocialMediaUser = {
  id: number;
  userName: string;
  realName: string;
  email: string;
  externalId: string;
  socialMediaTypeName: string;
  profilePictureUrl: string;
  linkedProfiles: SocialMediaUser[];
  followers: number;
  commentCount: number;
  blockStatus?: UserBlockStatus;
  timeToExpire?: number;
  tags: string[];
};

export type SocialMediaUserResponse = {
  socialMediaUsers: SocialMediaUser[];
  userCount: number;
};

export type SocialMediaUserCounts = {
  folders: Record<string, number>;
  watchlists: Record<string, number>;
};

type SocialMediaUserCountsResponse = {
  activeUserCount: number;
  blockedUserCount: number;
  totalUserCount: number;
};

export type SocialMediaUserLogBookEntry = {
  id: string;
  appUserId: string;
  socialMediaUserId: string;
  createdAt: string;
  eventName: string;
  eventObject: LogBookEventObject;
};

export type LogBookEventObject = {
  commentId?: string;
  noteText?: string;
  isAdded?: boolean;
  isPinned?: boolean;
  isDeleted?: boolean;
  isHiding?: boolean;
  isBlocked?: boolean;
  blockId?: string;
  duration?: string;
  tag?: string;
  commentText?: string;
  appUserName?: string;
};

export type EnrichedSocialMediaUserLogBookEntry = {
  id: string;
  appUserId: string;
  socialMediaUserId: string;
  createdAt: string;
  eventName: string;
  eventObject: LogBookEventObject;
  appUser?: User;
  actorName?: string;
  text: string;
};

export type BlockSocialMediaUserPayload = {
  duration: string;
  reason: string;
  additionalInfo: string;
  isApproved?: boolean;
};

export type SocialMediaUserFilter = {
  from?: string;
  to?: string;
  tags?: string[];
  text?: string[];
  connection?: string;
  blockStates?: string[];
  expired?: boolean;
  order?: string;
  smuId?: number;
  smuName?: string;
  sort?: string;
  commentCount?: string;
};

export type SocialMediaUserTagsBody = {
  tag: string;
  isAdded: boolean;
};

export type CommentSummaryTag = {
  name: string;
  markupElements: string[];
  score: number;
};

export type UserProfileCommentPreviewDto = {
  id: string;
  postTitle: string;
  text: string;
  createdAt: string;
  updatedAt?: string | null;
  aiMetadata?: {
    safetyScore?: number | null;
    isUnsafe?: boolean | null;
    aiCoreResults?: {
      negative?: {
        tagName: string;
        tagScore: number;
        tagInfo: string[];
      }[];
      positive?: {
        tagName: string;
        tagScore: number;
        tagInfo: string[];
      }[];
    } | null;
  } | null;
};

export type CommentSummary = {
  id: string;
  title: string;
  text: string;
  displayedAt: string;
  safetyScore: number;
  isUnsafe: boolean;
  tags: CommentSummaryTag[];
};

const mapAiTagToCommentSummaryTag = (tag: {
  tagName: string;
  tagScore: number;
  tagInfo: string[];
}): CommentSummaryTag => ({
  name: tag.tagName,
  score: tag.tagScore,
  markupElements: Array.isArray(tag.tagInfo) ? tag.tagInfo.filter(Boolean) : [],
});

const mapAiMetadataToCommentSummaryTags = (
  aiMetadata?: UserProfileCommentPreviewDto["aiMetadata"],
): CommentSummaryTag[] => {
  const negativeTags = aiMetadata?.aiCoreResults?.negative ?? [];
  const positiveTags = aiMetadata?.aiCoreResults?.positive ?? [];

  return [...negativeTags, ...positiveTags].map(mapAiTagToCommentSummaryTag);
};

export const mapCommentToCommentSummary = (comment: Comment): CommentSummary => ({
  id: comment.id,
  title: comment.post.teaser,
  text: comment.text,
  displayedAt: comment.createdAt,
  safetyScore: comment.unsafetyScore,
  isUnsafe: comment.isUnsafe,
  tags: comment.tags,
});

export const mapUserProfileCommentPreviewToCommentSummary = (
  comment: UserProfileCommentPreviewDto,
): CommentSummary => ({
  id: comment.id,
  title: comment.postTitle,
  text: comment.text,
  displayedAt: comment.createdAt,
  safetyScore: comment.aiMetadata?.safetyScore ?? -1,
  isUnsafe: comment.aiMetadata?.isUnsafe ?? false,
  tags: mapAiMetadataToCommentSummaryTags(comment.aiMetadata),
});
export const getUsers = async (): Promise<User[]> => {
  const response = await authorizedFetch(`${urls.users}?offset=0&limit=100`);
  const users = await response.json();
  return users.appUsers;
};

export const getUser = async (id: number): Promise<User> => {
  const response = await authorizedFetch(`${urls.users}/${id}`);
  const user = await response.json();
  return user;
};

export const editUser = async (id: number, user: Partial<User>) => {
  const editedUser = await authorizedFetch(`${urls.users}/${id}`, {
    body: JSON.stringify(user),
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
    },
  });
};

export const deleteUser = async (id: number) => {
  await authorizedFetch(`${urls.users}/${id}`, {
    method: "DELETE",
  });
};

export const getCurrentUser = async (): Promise<User> => {
  const response = await authorizedFetch(urls.profile).catch((result: Response) => {
    if (result.status === 403) {
      authService.logout();
    }
    throw result;
  });

  return response.json();
};

export const getSocialMediaUser = async (
  id: string,
  socialMediaTypeId?: number,
): Promise<SocialMediaUser> => {
  const params = new URLSearchParams();
  if (socialMediaTypeId !== undefined) params.set("smt", socialMediaTypeId.toString());
  const query = params.size > 0 ? `?${params.toString()}` : "";
  const response = await authorizedFetch(`${urls.socialMediaUsers}/${id}${query}`);
  return response.json();
};

export const getSocialMediaUsers = async (
  socialMediaConnectionId: number,
  socialMediaTypeId: number,
  page: number,
  itemsPerPage: number = 40,
  filter?: SocialMediaUserFilter,
): Promise<SocialMediaUserResponse> => {
  const params = new URLSearchParams();
  params.set("smc", socialMediaConnectionId.toString());
  params.set("smt", socialMediaTypeId.toString());
  if (filter?.from) params.set("from", filter.from);
  if (filter?.to) params.set("to", filter.to);
  if (filter?.tags) params.set("tags", filter.tags.join(","));
  if (filter?.text) params.set("text", filter.text.join(","));
  if (filter?.order) params.set("order", filter.order);
  if (filter?.sort) params.set("sort", filter.sort);
  if (filter?.smuName) params.set("smu-name", filter.smuName);
  if (filter?.smuId) params.set("smu-id", filter.smuId.toString());
  if (filter?.connection) params.set("connection", filter.connection);
  if (filter?.blockStates) params.set("block-state", filter.blockStates.join(","));
  if (filter?.commentCount) params.set("with-comment-count", filter.commentCount);
  if (filter?.expired) params.set("expired", filter.expired.toString());

  params.set("page", page.toString());
  params.set("size", itemsPerPage.toString());

  const response = await authorizedFetch(`${urls.socialMediaUsers}?${params.toString()}`);
  const users = await response.json();
  return users;
};

export const getNumberOfSocialMediaUsers = async (
  socialMediaTypeId: number,
  socialMediaConnectionId: number,
): Promise<SocialMediaUserCounts> => {
  const params = new URLSearchParams();
  params.set("smc", socialMediaConnectionId.toString());
  params.set("smt", socialMediaTypeId.toString());

  const response = await authorizedFetch(`${urls.socialMediaUsers}/count?${params.toString()}`);
  const counts: SocialMediaUserCountsResponse = await response.json();

  return {
    folders: {
      active: counts.activeUserCount,
      blocked: counts.blockedUserCount,
      all: counts.totalUserCount,
    },
    watchlists: {},
  };
};

export const getSocialMediaUserLogBook = async (
  id: string,
  socialMediaTypeId: number,
): Promise<EnrichedSocialMediaUserLogBookEntry[]> => {
  const params = new URLSearchParams();
  params.set("smt", socialMediaTypeId.toString());
  const response = await authorizedFetch(
    `${urls.socialMediaUsers}/${id}/logbook?${params.toString()}`,
  );

  const logBookEntries = (await response.json()) as SocialMediaUserLogBookEntry[];

  // Enriching actor names requires the admin-only user list endpoint. A failure here (e.g. 403 for non-admin users) must not discard the whole logbook, so we
  // fall back to the actor name provided on the entry itself.

  const users =
    logBookEntries.length > 0 ? await getUsers().catch(() => [] as User[]) : [];

  return logBookEntries.map((entry: SocialMediaUserLogBookEntry) => {
    const appUser = users.find((user) => String(user.id) === entry.appUserId);

    return {
      ...entry,
      appUser,
      actorName:
        appUser !== undefined
          ? `${appUser.firstName} ${appUser.lastName}`.trim()
          : entry.eventObject.appUserName,
      text: entry.eventObject.commentText ?? entry.eventObject.noteText ?? "",
    };
  });
};

export const getCommentsBySocialMediaUser = async (
  id: string,
  socialMediaTypeId: number,
): Promise<CommentSummary[]> => {
  const params = new URLSearchParams();
  params.set("smt", socialMediaTypeId.toString());
  const response = await authorizedFetch(
    `${urls.socialMediaUsers}/${id}/comments?${params.toString()}`,
  );
  const comments: UserProfileCommentPreviewDto[] = await response.json();
  return comments.map(mapUserProfileCommentPreviewToCommentSummary);
};

export const getPinnedCommentsBySocialMediaUser = async (
  id: string,
  socialMediaType: number,
): Promise<CommentSummary[]> => {
  const params = new URLSearchParams({ "smt": socialMediaType.toString() });
  const response = await authorizedFetch(
    `${urls.socialMediaUsers}/${id}/pinned-comments?${params}`,
  );
  const comments: UserProfileCommentPreviewDto[] = await response.json();
  return comments.map(mapUserProfileCommentPreviewToCommentSummary);
};

export const submitLogBookNote = async (
  id: string,
  socialMediaTypeId: number,
  noteText: string,
): Promise<void> => {
  const params = new URLSearchParams();
  params.set("smt", socialMediaTypeId.toString());

  await authorizedFetch(`${urls.socialMediaUsers}/${id}/note?${params.toString()}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ noteText }),
  });
  return;
};
export const linkSocialMediaUserProfile = async (
  socialMediaUserId: number,
  profileIdOrUrl?: number | string,
): Promise<void> => {
  const key = typeof profileIdOrUrl === "number" ? "id" : "link";
  await authorizedFetch(`${urls.socialMediaUsers}/${socialMediaUserId}/link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ [key]: profileIdOrUrl }),
  });
  return;
};

export const unlinkSocialMediaUserProfile = async (socialMediaUserId: number): Promise<void> => {
  await authorizedFetch(`${urls.socialMediaUsers}/${socialMediaUserId}/link`, {
    method: "DELETE",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ id: socialMediaUserId }),
  });
  return;
};

export const getAllSocialMediaUserTags = async (): Promise<string[]> => {
  const response = await authorizedFetch(`${urls.socialMediaUsers}/tags`);
  const tags = await response.json();
  return tags.tags;
};

export const getSocialMediaUserTagsPerUser = async (userId: number): Promise<string[]> => {
  const response = await authorizedFetch(`${urls.socialMediaUsers}/${userId}/tags`);
  const tags = await response.json();
  return tags.tags;
};

export const createOrDeleteSocialMediaUserTags = async (
  body: SocialMediaUserTagsBody,
): Promise<void> => {
  await authorizedFetch(`${baseUrlV1}/admin/tags`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return;
};

export const addOrRemoveSocialMediaUserTagsFromUser = async (
  userId: number,
  body: SocialMediaUserTagsBody,
): Promise<void> => {
  await authorizedFetch(`${urls.socialMediaUsers}/${userId}/tags`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return;
};

export const blockSocialMediaUser = async (
  admin: boolean,
  socialMediaUserId: number,
  socialMediaTypeId: number,
  body: BlockSocialMediaUserPayload,
): Promise<void> => {
  const url = admin ? urls.adminSocialMediaUsers : urls.socialMediaUsers;
  const params = new URLSearchParams({
    smt: socialMediaTypeId.toString(),
  });

  await authorizedFetch(`${url}/${socialMediaUserId}/block?${params.toString()}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  return;
};

export const unblockSocialMediaUser = async (
  socialMediaUserId: number,
  socialMediaTypeId: number,
): Promise<void> => {
  const params = new URLSearchParams({
    smt: socialMediaTypeId.toString(),
  });

  await authorizedFetch(
    `${urls.adminSocialMediaUsers}/${socialMediaUserId}/unblock?${params.toString()}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
    },
  );
  return;
};

export const getSocialMediaUsersBlockInfo = async () => {
  const response = await authorizedFetch(`${urls.socialMediaUsers}/block-info`);
  return response.json();
};
